import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { configureAnchors } from 'react-scrollable-anchor';
import TabView from '../../../components/TabView';
import {
  getCRSClusterViewDescriptionCodesData,
  getCRSFeatureCategoryCodesData,
  getCRSFeatureCodesData,
  getCRSTransactionProductMappingData,
  getCRSTransactionTypesData,
  getCustomerProfileData
} from '../../../redux/actions/crs/crsRiskDetail';
import { getGlobalSettingsPageInfo } from '../../../redux/actions/settings';
import { getCustomerKeyFromURL } from '../../../services/util';
import {
  CRS_CONSTANTS,
  CRS_TAB_PAGEID_LIST
} from '../../constants/crsContants';
import AssocCustomers from './AssocCustomers';
import AssocTransaction from './AssocTransaction';
import CRSDetailHeader from './crsDetailHeader';
import TrendAnalysisComponent from './CRSTrendAnalysis';
import CustomerProfileView from './CustomerProfileView';
import HistAlert from './HistAlert';
import style from './index.scss';
import { processDataFromProps } from './riskDetailUtil';
import ScoreExplainability from './ScoreExplainability';
import { getAMLSCommonAlertDetails } from '../../../redux/actions/common';
import ExportHistory from '../../common/TransactionDetalis/ExportHistory';
import {
  TRANSACTION_HISTORY_MODULES as MODULES,
  resolveDownloadModule
} from '../../constants/commonConstants';

const { GET_DATA } = CRS_CONSTANTS;
const GLOBAL_SETTING_PAGE_ID = CRS_TAB_PAGEID_LIST[1];

// const isPrioritizedAlert = true;

const navigationTabList = [
  'Profile',
  'Trend Analysis and Risk Lineage',
  'Score Explainability',
  'Transaction Details',
  'Alert History',
  'Transaction Download History'
];

const prioritizedNavigationList = [
  'Profile',
  'Trend Analysis and Risk Lineage',
  'Score Explainability',
  'Transaction Details',
  'Alert History',
  'Transaction Download History'
];

const navigationGSConstants = [
  true, // -> 'Profile',
  true, // -> 'Trend Analysis and Risk Lineage',
  true, // -> 'Score Explainability',
  {
    widgetGroupId: 'CRS_TRANSACTION_DETAILS_GROUP',
    widgetId: 'CRS_TRANSACTION_DETAILS_WIDGET'
  }, // -> 'Transaction Details',
  {
    widgetGroupId: 'CRS_ALERT_HISTORY_GROUP',
    widgetId: 'CRS_ALERT_HISTORY_WIDGET'
  }, // -> 'Alert History'
  true // -> 'Transaction Download History'
];

const prioritizedNavigationGSConstants = [
  true, // -> 'Profile',
  false, // -> 'Trend Analysis and Risk Lineage',
  true, // -> 'Score Explainability',
  {
    widgetGroupId: 'CRS_TRANSACTION_DETAILS_GROUP',
    widgetId: 'CRS_TRANSACTION_DETAILS_WIDGET'
  }, // -> 'Transaction Details',
  {
    widgetGroupId: 'CRS_ALERT_HISTORY_GROUP',
    widgetId: 'CRS_ALERT_HISTORY_WIDGET'
  }, // -> 'Alert History'
  true // -> 'Transaction Download History'
];

/**
 * CRSRiskDetail Page contains all the Widgets required for CRS
 */
class CRSRiskDetail extends Component {
  /**
   * constructor
   * @param props
   */
  constructor(props) {
    super(props);
    this.customerKey = props.isEmbedded
      ? props.customerId
      : getCustomerKeyFromURL();
    const urlParams = new URLSearchParams(window.location.search);
    this.state = {
      selectedTab: 0,
      isPrioritizedAlert: false,
      initialTab: urlParams.get('tab') || null
    };
  }

  /**
   * componentDidMount
   * Added all the necessary Dispatch Items items
   * Here like CODES API and Mappings
   */
  componentDidMount() {
    window.scrollTo(0, 0);
    configureAnchors({ offset: -60, scrollDuration: 300 });

    // Reset the tracking variables on mount
    this.prevWindowSearch = '';
    this.downloadHistoryHandled = false;

    // Check if tab parameter is in URL on mount
    const currentSearch = window.location.search;
    const urlParams = new URLSearchParams(currentSearch);
    const tab = urlParams.get('tab');
    if (tab === 'downloadHistory') {
      this.setState({ initialTab: 'downloadHistory' });
    }

    this.props.dispatch(
      getCustomerProfileData({ customerKey: this.customerKey }, GET_DATA)
    );
    this.props.dispatch(getCRSFeatureCodesData({}, GET_DATA));
    this.props.dispatch(getCRSTransactionTypesData({}, GET_DATA));
    this.props.dispatch(getCRSFeatureCategoryCodesData({}, GET_DATA));
    this.props.dispatch(getCRSClusterViewDescriptionCodesData({}, GET_DATA));
    this.props.dispatch(getCRSTransactionProductMappingData({}, GET_DATA));

    this.props.dispatch(
      getGlobalSettingsPageInfo({
        pageId: GLOBAL_SETTING_PAGE_ID
      })
    );
    if (this.props.isEmbedded) {
      this.props.dispatch(
        getAMLSCommonAlertDetails({ alertId: this.props.alertId })
      );
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isEmbedded) {
      this.setState({
        isPrioritizedAlert:
          nextProps?.alertDetails?.data?.alertType === 'CRS_PRIORITISE_ALERT'
            ? true
            : false
      });
    }
    if (this.customerKey !== nextProps?.customerId && nextProps.isEmbedded) {
      const customerKey = nextProps?.customerId;
      window.scrollTo(0, 0);
      configureAnchors({ offset: -60, scrollDuration: 300 });
      this.props.dispatch(getCustomerProfileData({ customerKey }, GET_DATA));
      this.props.dispatch(
        getGlobalSettingsPageInfo({
          pageId: GLOBAL_SETTING_PAGE_ID
        })
      );

      this.customerKey = customerKey;

      // Reset the downloadHistory handling flag when customer changes
      this.downloadHistoryHandled = false;

      // Check if tab parameter is in URL
      const currentSearch = window.location.search;
      const urlParams = new URLSearchParams(currentSearch);
      const tab = urlParams.get('tab');
      const initialTab = tab === 'downloadHistory' ? 'downloadHistory' : null;

      this.setState({
        selectedTab: 0,
        initialTab
      });

      this.props.dispatch(
        getAMLSCommonAlertDetails({ alertId: nextProps?.alertId })
      );
    }
  }

  componentDidUpdate(prevProps) {
    // Use window.location.search as it's always reliable
    const currentSearch = window.location.search;
    const prevSettingsData = prevProps.settings?.configuration?.data;
    const currentSettingsData = this.props.settings?.configuration?.data;

    // Reset downloadHistoryHandled when the URL search changes (e.g. new refresh param
    // from a notification click while already on this page)
    if (currentSearch !== this.prevWindowSearch) {
      this.prevWindowSearch = currentSearch;
      this.downloadHistoryHandled = false;
    }

    // Check if URL contains downloadHistory tab param and we haven't handled it yet
    const urlParams = new URLSearchParams(currentSearch);
    const tab = urlParams.get('tab');
    const shouldHandleDownloadHistory = tab === 'downloadHistory' && !this.downloadHistoryHandled;

    if (shouldHandleDownloadHistory) {
      // Directly switch to tab if settings are available
      if (currentSettingsData) {
        this.downloadHistoryHandled = true;
        this.switchToDownloadHistoryTab();
      } else {
        // Otherwise, set initialTab and wait for settings to load
        this.setState({ initialTab: 'downloadHistory' });
        this.downloadHistoryHandled = true;
      }
    }

    // Also check when settings just loaded and initialTab is downloadHistory
    if (
      !prevSettingsData &&
      currentSettingsData &&
      this.state.initialTab === 'downloadHistory'
    ) {
      this.switchToDownloadHistoryTab();
    }
  }

  switchToDownloadHistoryTab = () => {
    const { isPrioritizedAlert } = this.state;
    const tabList = isPrioritizedAlert
      ? prioritizedNavigationList
      : navigationTabList;
    const gsConstants = isPrioritizedAlert
      ? prioritizedNavigationGSConstants
      : navigationGSConstants;

    const filteredTabs = tabList.filter((_tab, index) => {
      if (gsConstants[index] !== true) {
        return this.isGSVisible(gsConstants[index]);
      }
      return gsConstants[index];
    });

    const downloadHistoryTab = filteredTabs.indexOf(
      'Transaction Download History'
    );
    if (downloadHistoryTab !== -1) {
      this.setState({ selectedTab: downloadHistoryTab, initialTab: null });
    }
  };

  onNavigationTabSelect = (index) => {
    this.setState({
      selectedTab: index
    });
  };

  getGSConfig = (q) => {
    if (!q) {
      return {};
    }
    if (this.props.settings?.configuration?.data?.uiWidgetGroups) {
      const groups = this.props.settings.configuration.data.uiWidgetGroups;
      return groups
        .find((g) => g.widgetGroupId === q.widgetGroupId)
        ?.widgets.find((w) => w.widgetId === q.widgetId);
    }
    return {};
  };

  isGSVisible = (q) => {
    if (!q) {
      return false;
    }
    if (this.props.settings?.configuration?.data?.uiWidgetGroups) {
      const groups = this.props.settings.configuration.data.uiWidgetGroups;
      return groups
        .find((g) => g.widgetGroupId === q.widgetGroupId)
        ?.widgets.find((w) => w.widgetId === q.widgetId)?.isVisible;
    }
    return false;
  };

  /**
   * crsRiskDetailData
   * @returns {div}
   */
  render() {
    const { selectedTab, isPrioritizedAlert } = this.state;
    const { crsRiskDetailData } = this.props;
    const effectiveModule =
      this.props.module ||
      resolveDownloadModule({ isFromCM: this.props.isFromCM, isCrsAlert: true });
    const {
      featureCodesResponse,
      crsTransactionTypesResponse,
      crsFeatureCategoryCodesData,
      customerProfileData
    } = processDataFromProps(crsRiskDetailData);

    // select which navigation tab and GS constants to select when prioritized alert is there
    const updatedNavigationTabList = isPrioritizedAlert
      ? prioritizedNavigationList
      : navigationTabList;
    const updatedNavigationGSContants = isPrioritizedAlert
      ? prioritizedNavigationGSConstants
      : navigationGSConstants;

    const getGSConstantByTab = (tabName) =>
      updatedNavigationGSContants[updatedNavigationTabList.indexOf(tabName)];

    const filteredNavigationTabList = updatedNavigationTabList.filter(
      (tab, index) => {
        if (updatedNavigationGSContants[index] !== true) {
          return this.isGSVisible(updatedNavigationGSContants[index]);
        }
        return updatedNavigationGSContants[index];
      }
    );

    // add new constants for tabIndexes for diff tabs (must align with filteredNavigationTabList)
    const customerTab = filteredNavigationTabList.indexOf('Profile');
    const trendAnalysisTab = filteredNavigationTabList.indexOf(
      'Trend Analysis and Risk Lineage'
    );
    const explainabilityTab = filteredNavigationTabList.indexOf(
      'Score Explainability'
    );
    const transactionDetailsTab = filteredNavigationTabList.indexOf(
      'Transaction Details'
    );
    const alertHistoryTab = filteredNavigationTabList.indexOf('Alert History');
    const downloadHistoryTab = filteredNavigationTabList.indexOf(
      'Transaction Download History'
    );

    const { data } = this.props.alertDetails;
    const alertDate = data?.alertDate;

    return (
      <div
        className={cx({
          [style.embedContainer]: this.props.isEmbedded
        })}>
        <CRSDetailHeader
          customerProfileData={customerProfileData}
          isEmbedded={this.props.isEmbedded}
          alertId={this.props.alertId}
          alertMetaSuffix={this.props.alertMetaSuffix}
          alertMetaPrefix={this.props.alertMetaPrefix}
          alertDetails={this.props.alertDetails}
        />
        <div className={style.tabViewContainer}>
          <TabView
            defaultIndex={selectedTab}
            tabList={filteredNavigationTabList}
            onTabSelect={this.onNavigationTabSelect}
          />
        </div>
        <div className={style.riskDetailInnerContainer}>
          {selectedTab === 0 && (
            <div id={'section0'}>
              <CustomerProfileView
                customerProfileData={customerProfileData}
                GSConfigIndividual={this.getGSConfig({
                  widgetGroupId: 'CRS_CUSTOMER_PROFILE_GROUP',
                  widgetId: 'CRS_CUSTOMER_DETAILS_WIDGET_I'
                })}
                GSConfigCorporate={this.getGSConfig({
                  widgetGroupId: 'CRS_CUSTOMER_PROFILE_GROUP',
                  widgetId: 'CRS_CUSTOMER_DETAILS_WIDGET_C'
                })}
              />
              <AssocCustomers
                customerKey={this.customerKey}
                GSConfig={this.getGSConfig({
                  widgetGroupId: 'CRS_CUSTOMER_PROFILE_GROUP',
                  widgetId: 'CRS_ASSOCIATED_CUSTOMER_WIDGET'
                })}
              />
            </div>
          )}

          {selectedTab === trendAnalysisTab &&
            trendAnalysisTab !== -1 &&
            !isPrioritizedAlert && (
              // this.isGSVisible(updatedNavigationGSContants[1]) && (
              <div id={'section1'}>
                <TrendAnalysisComponent
                  customerKey={this.customerKey}
                  crsFeatureCategoryCodesData={crsFeatureCategoryCodesData}
                />
              </div>
            )}

          {selectedTab === explainabilityTab && explainabilityTab !== -1 && (
            <div id={isPrioritizedAlert ? 'section1' : 'section2'}>
              <ScoreExplainability
                isPrioritizedAlert={isPrioritizedAlert}
                alertDetails={this.props.alertDetails}
                customerKey={this.customerKey}
                GSConfig={this.getGSConfig({
                  widgetGroupId: 'CRS_SCORE_EXPLANABILITY_GROUP',
                  widgetId: 'CRS_SCORE_EXPLANABILITY_WIDGET'
                })}
              />
            </div>
          )}

          {transactionDetailsTab !== -1 &&
            selectedTab === transactionDetailsTab &&
            this.isGSVisible(getGSConstantByTab('Transaction Details')) && (
              <div id={isPrioritizedAlert ? 'section2' : 'section3'}>
                <AssocTransaction
                  alertId={this.props.alertId}
                  customerKey={this.customerKey}
                  GSConfig={this.getGSConfig({
                    widgetGroupId: 'CRS_TRANSACTION_DETAILS_GROUP',
                    widgetId: 'CRS_TRANSACTION_DETAILS_WIDGET'
                  })}
                  alertDate={alertDate}
                  isFromCM={this.props.isFromCM}
                  module={effectiveModule}
                  caseId={this.props.caseId}
                />
              </div>
            )}

          {selectedTab === alertHistoryTab &&
            alertHistoryTab !== -1 &&
            this.isGSVisible(getGSConstantByTab('Alert History')) && (
              <div id={isPrioritizedAlert ? 'section3' : 'section4'}>
                <HistAlert
                  customerKey={this.customerKey}
                  GSConfig={this.getGSConfig({
                    widgetGroupId: 'CRS_ALERT_HISTORY_GROUP',
                    widgetId: 'CRS_ALERT_HISTORY_WIDGET'
                  })}
                />
              </div>
            )}

          {selectedTab === downloadHistoryTab && downloadHistoryTab !== -1 && (
            <div id={isPrioritizedAlert ? 'section4' : 'section5'}>
              <ExportHistory
                alertId={this.props.alertId}
                partyId={this.customerKey}
                isActive={selectedTab === downloadHistoryTab}
                module={effectiveModule}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

CRSRiskDetail.propTypes = {
  dispatch: PropTypes.func,
  settings: PropTypes.object,
  crsRiskDetailData: PropTypes.object,
  isEmbedded: PropTypes.bool,
  alertId: PropTypes.string,
  customerId: PropTypes.string,
  alertMetaSuffix: PropTypes.element,
  alertMetaPrefix: PropTypes.element,
  alertDetails: PropTypes.object
};

/**
 * mapStateToProps
 * @param state
 * @param ownProps
 * @returns {{crsRiskDetailData: crsRiskDetailReducer}}
 */
function mapStateToProps(state, ownProps = {}) {
  return {
    crsRiskDetailData: state.crsRiskDetailReducer,
    settings: state.settings,
    alertDetails: state.amlsCommon.alertDetails,
    location: ownProps.location
  };
}

/**
 * mapDispatchToProps
 * @param dispatch
 * @param ownProps
 * @returns {{dispatch: *, navigateTo: navigateTo}}
 */
function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch
  };
}

/**
 * connect function for CRSRiskDetail page
 */
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CRSRiskDetail)
);
